// src/components/(blog)/post/PostComments.tsx

'use client';

import { useEffect, useState } from 'react';
import { MotionConfig } from 'motion/react';
import RatingStars from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/RatingStars';
import AverageStars from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/AverageStars';
import { toPersianDigits, toPersianNumber } from '@/utils/persianUtils';
import PostCommentForm from '@/components/(blog)/post/PostCommentForm';
import { PostRatings } from '@/types/blog/single';
import {
    CommentNode,
    CommentsData,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reviews';

interface PostCommentsProps {
    currentPostId: number;
    postRatings: PostRatings | undefined;
    commentCount: number;
    displayName?: string;
    customName?: string;
    comments?: CommentNode[];
    hasNextPage?: CommentsData['comments']['pageInfo']['hasNextPage'];
}

const PostComments: React.FC<PostCommentsProps> = ({
    currentPostId,
    postRatings,
    commentCount,
    displayName,
    comments,
    hasNextPage,
    customName,
}) => {
    const [showingData, setShowingData] = useState<{ average: number; count: number }>({
        average: Number(postRatings?.average) || 0,
        count: Number(postRatings?.count) || 0,
    });

    useEffect(() => {
        setShowingData({
            average: Number(postRatings?.average) || 0,
            count: Number(postRatings?.count) || 0,
        });
    }, [currentPostId, postRatings]);

    // RatingStars sends the rating on click (login asked for when needed) and reports the new
    // totals. Never let the shown numbers drop: some posts show stored totals the API may not
    // report (the drip-feed plan).
    const handleRated = ({
        rating,
        previous,
        average,
        count,
    }: {
        rating: number;
        previous: number | null;
        average: number;
        count: number;
    }) => {
        setShowingData((old) => {
            if (count >= old.count) return { average: average || old.average, count };
            const isNew = previous === null;
            const n = old.count + (isNew ? 1 : 0);
            const sum = old.average * old.count - (isNew ? 0 : previous || 0) + rating;
            return { average: n ? sum / n : rating, count: n };
        });
    };

    const subject = displayName ? displayName : customName ? `این ${customName}` : 'این مقاله';

    // Rendered on the server too (it used to wait for the client, so the comments below were
    // missing from the HTML crawlers read).
    return (
        <>
            <MotionConfig reducedMotion='user'>
                <div
                    id='comments'
                    style={{ scrollMarginTop: '200px' }}
                    className='mb-4 grid w-full gap-4 rounded-2xl border border-black/5 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5 md:grid-cols-[1fr_auto] md:items-center lg:p-5'
                >
                    <div className='flex flex-col gap-2 max-md:items-center max-md:text-center'>
                        <p className='text-base font-semibold lg:text-xl'>
                            {displayName
                                ? `تحلیل و پیش‌بینی شما درباره قیمت ${displayName} چیست؟`
                                : customName
                                  ? `نظر شما درباره این ${customName} چیست؟`
                                  : `نظر شما درباره این مقاله چیست؟`}
                        </p>
                        <div
                            className='flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-white/70 max-md:justify-center'
                            aria-label={`میانگین امتیاز ${subject}: ${showingData.average.toFixed(1)} از ${showingData.count} رأی`}
                        >
                            <span className='text-2xl font-bold leading-none text-gray-900 dark:text-white'>
                                {toPersianNumber(showingData.average.toFixed(1))}
                            </span>
                            <AverageStars value={showingData.average} size='size-4' />
                            <span>از {toPersianDigits(String(showingData.count))} رأی</span>
                        </div>
                    </div>
                    <div className='flex flex-col items-center gap-1'>
                        <p className='text-sm text-gray-600 dark:text-white/70'>
                            شما چه امتیازی می‌دهید؟
                        </p>
                        <RatingStars currentPostId={currentPostId} onRating={handleRated} />
                    </div>
                </div>
            </MotionConfig>

            <PostCommentForm
                postId={currentPostId}
                commentCount={commentCount}
                comments={comments}
                hasNextPage={hasNextPage}
            />
        </>
    );
};

export default PostComments;
